/**
 * California Congressional Districts - 2026 Cycle Data
 *
 * ============================================================================
 * IMPORTANT: CALIFORNIA DATA EXCEPTION
 * ============================================================================
 *
 * California candidate data is stored in the `candidates` table, NOT in
 * `all_candidates_2026` like other states. This is because California has
 * a unique data pipeline.
 *
 * DATA STRUCTURE:
 * - `candidates` table: Contains all California congressional candidates
 * - `representatives` table: Links candidates to districts via `representative_id`
 * - Join: candidates.representative_id -> representatives.id
 * - District: representatives.district_number gives the district (e.g., 17 -> CA-17)
 *
 * QUERY PATTERN:
 * ```sql
 * SELECT c.*, r.district_number, 'CA-' || r.district_number as district_code
 * FROM candidates c
 * JOIN representatives r ON c.representative_id = r.id
 * WHERE r.office = 'U.S. Representative'
 * ```
 *
 * This file provides DISTRICT-LEVEL fallback data only (ratings, margins, etc.)
 * when the districts table is incomplete. Candidate data always comes from
 * the database.
 *
 * Sources:
 * - California Secretary of State
 * - Cook Political Report
 * - 538/ABC News
 * ============================================================================
 */

export const CALIFORNIA_DISTRICTS = [
  {
    district_code: 'CA-1',
    name: 'California 1st Congressional District',
    state: 'CA',
    incumbent_name: 'Doug LaMalfa',
    incumbent_party: 'Republican',
    model_rating: 'Safe R',
    margin_2024: -19.2,
    pvi: 'R+10',
    is_open_seat: false
  },
  {
    district_code: 'CA-2',
    name: 'California 2nd Congressional District',
    state: 'CA',
    incumbent_name: 'Jared Huffman',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 35.6,
    pvi: 'D+19',
    is_open_seat: false
  },
  {
    district_code: 'CA-3',
    name: 'California 3rd Congressional District',
    state: 'CA',
    incumbent_name: 'Kevin Kiley',
    incumbent_party: 'Republican',
    model_rating: 'Likely R',
    margin_2024: -9.8,
    pvi: 'R+5',
    is_open_seat: false
  },
  {
    district_code: 'CA-4',
    name: 'California 4th Congressional District',
    state: 'CA',
    incumbent_name: 'Mike Thompson',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 24.3,
    pvi: 'D+14',
    is_open_seat: false
  },
  {
    district_code: 'CA-5',
    name: 'California 5th Congressional District',
    state: 'CA',
    incumbent_name: 'Tom McClintock',
    incumbent_party: 'Republican',
    model_rating: 'Safe R',
    margin_2024: -17.4,
    pvi: 'R+9',
    is_open_seat: false
  },
  {
    district_code: 'CA-6',
    name: 'California 6th Congressional District',
    state: 'CA',
    incumbent_name: 'Ami Bera',
    incumbent_party: 'Democratic',
    model_rating: 'Likely D',
    margin_2024: 8.9,
    pvi: 'D+4',
    is_open_seat: false
  },
  {
    district_code: 'CA-7',
    name: 'California 7th Congressional District',
    state: 'CA',
    incumbent_name: 'Doris Matsui',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 38.2,
    pvi: 'D+22',
    is_open_seat: false
  },
  {
    district_code: 'CA-8',
    name: 'California 8th Congressional District',
    state: 'CA',
    incumbent_name: 'John Garamendi',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 17.6,
    pvi: 'D+9',
    is_open_seat: false
  },
  {
    district_code: 'CA-9',
    name: 'California 9th Congressional District',
    state: 'CA',
    incumbent_name: 'Josh Harder',
    incumbent_party: 'Democratic',
    model_rating: 'Lean D',
    margin_2024: 5.2,
    pvi: 'D+2',
    is_open_seat: false
  },
  {
    district_code: 'CA-10',
    name: 'California 10th Congressional District',
    state: 'CA',
    incumbent_name: 'Mark DeSaulnier',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 42.1,
    pvi: 'D+25',
    is_open_seat: false
  },
  {
    district_code: 'CA-11',
    name: 'California 11th Congressional District',
    state: 'CA',
    incumbent_name: 'Nancy Pelosi',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 62.4,
    pvi: 'D+38',
    is_open_seat: false
  },
  {
    district_code: 'CA-12',
    name: 'California 12th Congressional District',
    state: 'CA',
    incumbent_name: 'Lateefah Simon',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 58.7,
    pvi: 'D+36',
    is_open_seat: false
  },
  {
    district_code: 'CA-13',
    name: 'California 13th Congressional District',
    state: 'CA',
    incumbent_name: 'Adam Gray',
    incumbent_party: 'Democratic',
    model_rating: 'Lean D',
    margin_2024: 0.4,
    pvi: 'EVEN',
    is_open_seat: false
  },
  {
    district_code: 'CA-14',
    name: 'California 14th Congressional District',
    state: 'CA',
    incumbent_name: 'Eric Swalwell',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 35.8,
    pvi: 'D+20',
    is_open_seat: false
  },
  {
    district_code: 'CA-15',
    name: 'California 15th Congressional District',
    state: 'CA',
    incumbent_name: 'Kevin Mullin',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 44.3,
    pvi: 'D+26',
    is_open_seat: false
  },
  {
    district_code: 'CA-16',
    name: 'California 16th Congressional District',
    state: 'CA',
    incumbent_name: 'Sam Liccardo',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 25.1,
    pvi: 'D+14',
    is_open_seat: false
  },
  {
    district_code: 'CA-17',
    name: 'California 17th Congressional District',
    state: 'CA',
    incumbent_name: 'Ro Khanna',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 46.2,
    pvi: 'D+27',
    is_open_seat: false
  },
  {
    district_code: 'CA-18',
    name: 'California 18th Congressional District',
    state: 'CA',
    incumbent_name: 'Zoe Lofgren',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 32.4,
    pvi: 'D+18',
    is_open_seat: false
  },
  {
    district_code: 'CA-19',
    name: 'California 19th Congressional District',
    state: 'CA',
    incumbent_name: 'Jimmy Panetta',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 28.6,
    pvi: 'D+16',
    is_open_seat: false
  },
  {
    district_code: 'CA-20',
    name: 'California 20th Congressional District',
    state: 'CA',
    incumbent_name: 'Vince Fong',
    incumbent_party: 'Republican',
    model_rating: 'Safe R',
    margin_2024: -24.8,
    pvi: 'R+14',
    is_open_seat: false
  },
  {
    district_code: 'CA-21',
    name: 'California 21st Congressional District',
    state: 'CA',
    incumbent_name: 'Jim Costa',
    incumbent_party: 'Democratic',
    model_rating: 'Likely D',
    margin_2024: 7.3,
    pvi: 'D+3',
    is_open_seat: false
  },
  {
    district_code: 'CA-22',
    name: 'California 22nd Congressional District',
    state: 'CA',
    incumbent_name: 'David Valadao',
    incumbent_party: 'Republican',
    model_rating: 'Lean R',
    margin_2024: -2.8,
    pvi: 'R+1',
    is_open_seat: false
  },
  {
    district_code: 'CA-23',
    name: 'California 23rd Congressional District',
    state: 'CA',
    incumbent_name: 'Jay Obernolte',
    incumbent_party: 'Republican',
    model_rating: 'Safe R',
    margin_2024: -21.5,
    pvi: 'R+12',
    is_open_seat: false
  },
  {
    district_code: 'CA-24',
    name: 'California 24th Congressional District',
    state: 'CA',
    incumbent_name: 'Salud Carbajal',
    incumbent_party: 'Democratic',
    model_rating: 'Likely D',
    margin_2024: 10.4,
    pvi: 'D+5',
    is_open_seat: false
  },
  {
    district_code: 'CA-25',
    name: 'California 25th Congressional District',
    state: 'CA',
    incumbent_name: 'Raul Ruiz',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 14.2,
    pvi: 'D+7',
    is_open_seat: false
  },
  {
    district_code: 'CA-26',
    name: 'California 26th Congressional District',
    state: 'CA',
    incumbent_name: 'Julia Brownley',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 16.8,
    pvi: 'D+9',
    is_open_seat: false
  },
  {
    district_code: 'CA-27',
    name: 'California 27th Congressional District',
    state: 'CA',
    incumbent_name: 'George Whitesides',
    incumbent_party: 'Democratic',
    model_rating: 'Lean D',
    margin_2024: 2.1,
    pvi: 'D+1',
    is_open_seat: false
  },
  {
    district_code: 'CA-28',
    name: 'California 28th Congressional District',
    state: 'CA',
    incumbent_name: 'Judy Chu',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 34.5,
    pvi: 'D+19',
    is_open_seat: false
  },
  {
    district_code: 'CA-29',
    name: 'California 29th Congressional District',
    state: 'CA',
    incumbent_name: 'Tony Cardenas',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 45.7,
    pvi: 'D+27',
    is_open_seat: false
  },
  {
    district_code: 'CA-30',
    name: 'California 30th Congressional District',
    state: 'CA',
    incumbent_name: 'Laura Friedman',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 42.3,
    pvi: 'D+24',
    is_open_seat: false
  },
  {
    district_code: 'CA-31',
    name: 'California 31st Congressional District',
    state: 'CA',
    incumbent_name: 'Gil Cisneros',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 31.9,
    pvi: 'D+17',
    is_open_seat: false
  },
  {
    district_code: 'CA-32',
    name: 'California 32nd Congressional District',
    state: 'CA',
    incumbent_name: 'Brad Sherman',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 36.4,
    pvi: 'D+20',
    is_open_seat: false
  },
  {
    district_code: 'CA-33',
    name: 'California 33rd Congressional District',
    state: 'CA',
    incumbent_name: 'Pete Aguilar',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 20.5,
    pvi: 'D+11',
    is_open_seat: false
  },
  {
    district_code: 'CA-34',
    name: 'California 34th Congressional District',
    state: 'CA',
    incumbent_name: 'Jimmy Gomez',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 52.8,
    pvi: 'D+32',
    is_open_seat: false
  },
  {
    district_code: 'CA-35',
    name: 'California 35th Congressional District',
    state: 'CA',
    incumbent_name: 'Norma Torres',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 23.7,
    pvi: 'D+13',
    is_open_seat: false
  },
  {
    district_code: 'CA-36',
    name: 'California 36th Congressional District',
    state: 'CA',
    incumbent_name: 'Ted Lieu',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 40.1,
    pvi: 'D+23',
    is_open_seat: false
  },
  {
    district_code: 'CA-37',
    name: 'California 37th Congressional District',
    state: 'CA',
    incumbent_name: 'Sydney Kamlager-Dove',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 54.6,
    pvi: 'D+33',
    is_open_seat: false
  },
  {
    district_code: 'CA-38',
    name: 'California 38th Congressional District',
    state: 'CA',
    incumbent_name: 'Linda Sanchez',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 28.4,
    pvi: 'D+15',
    is_open_seat: false
  },
  {
    district_code: 'CA-39',
    name: 'California 39th Congressional District',
    state: 'CA',
    incumbent_name: 'Mark Takano',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 18.6,
    pvi: 'D+10',
    is_open_seat: false
  },
  {
    district_code: 'CA-40',
    name: 'California 40th Congressional District',
    state: 'CA',
    incumbent_name: 'Young Kim',
    incumbent_party: 'Republican',
    model_rating: 'Lean R',
    margin_2024: -5.8,
    pvi: 'R+2',
    is_open_seat: false
  },
  {
    district_code: 'CA-41',
    name: 'California 41st Congressional District',
    state: 'CA',
    incumbent_name: 'Ken Calvert',
    incumbent_party: 'Republican',
    model_rating: 'Lean R',
    margin_2024: -4.7,
    pvi: 'R+2',
    is_open_seat: false
  },
  {
    district_code: 'CA-42',
    name: 'California 42nd Congressional District',
    state: 'CA',
    incumbent_name: 'Robert Garcia',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 38.9,
    pvi: 'D+22',
    is_open_seat: false
  },
  {
    district_code: 'CA-43',
    name: 'California 43rd Congressional District',
    state: 'CA',
    incumbent_name: 'Maxine Waters',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 48.2,
    pvi: 'D+29',
    is_open_seat: false
  },
  {
    district_code: 'CA-44',
    name: 'California 44th Congressional District',
    state: 'CA',
    incumbent_name: 'Nanette Barragan',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 44.5,
    pvi: 'D+26',
    is_open_seat: false
  },
  {
    district_code: 'CA-45',
    name: 'California 45th Congressional District',
    state: 'CA',
    incumbent_name: 'Derek Tran',
    incumbent_party: 'Democratic',
    model_rating: 'Toss-Up',
    margin_2024: 0.2,
    pvi: 'EVEN',
    is_open_seat: false
  },
  {
    district_code: 'CA-46',
    name: 'California 46th Congressional District',
    state: 'CA',
    incumbent_name: 'Lou Correa',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 26.3,
    pvi: 'D+14',
    is_open_seat: false
  },
  {
    district_code: 'CA-47',
    name: 'California 47th Congressional District',
    state: 'CA',
    incumbent_name: 'Dave Min',
    incumbent_party: 'Democratic',
    model_rating: 'Likely D',
    margin_2024: 6.1,
    pvi: 'D+3',
    is_open_seat: false
  },
  {
    district_code: 'CA-48',
    name: 'California 48th Congressional District',
    state: 'CA',
    incumbent_name: 'Darrell Issa',
    incumbent_party: 'Republican',
    model_rating: 'Safe R',
    margin_2024: -15.3,
    pvi: 'R+8',
    is_open_seat: false
  },
  {
    district_code: 'CA-49',
    name: 'California 49th Congressional District',
    state: 'CA',
    incumbent_name: 'Mike Levin',
    incumbent_party: 'Democratic',
    model_rating: 'Lean D',
    margin_2024: 4.9,
    pvi: 'D+2',
    is_open_seat: false
  },
  {
    district_code: 'CA-50',
    name: 'California 50th Congressional District',
    state: 'CA',
    incumbent_name: 'Scott Peters',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 18.4,
    pvi: 'D+10',
    is_open_seat: false
  },
  {
    district_code: 'CA-51',
    name: 'California 51st Congressional District',
    state: 'CA',
    incumbent_name: 'Sara Jacobs',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 30.2,
    pvi: 'D+16',
    is_open_seat: false
  },
  {
    district_code: 'CA-52',
    name: 'California 52nd Congressional District',
    state: 'CA',
    incumbent_name: 'Juan Vargas',
    incumbent_party: 'Democratic',
    model_rating: 'Safe D',
    margin_2024: 35.8,
    pvi: 'D+20',
    is_open_seat: false
  }
];

/**
 * Get all California districts with fallback data
 * This provides district-level info (ratings, margins) when database is incomplete
 */
export function getCaliforniaDistricts() {
  return CALIFORNIA_DISTRICTS.map(d => ({
    ...d,
    id: `ca-fallback-${d.district_code}`,
    model_confidence: 0.75,
    filing_deadline: '2026-03-06',
    primary_date: '2026-06-02',
    general_date: '2026-11-03'
  }));
}

/**
 * Check if a district code is for California
 */
export function isCaliforniaDistrict(districtCode) {
  return districtCode?.startsWith('CA-');
}

/**
 * Get competitive California districts (for filtering)
 */
export function getCompetitiveCaliforniaDistricts() {
  return getCaliforniaDistricts().filter(d =>
    ['Lean D', 'Lean R', 'Toss-Up', 'Likely D', 'Likely R'].includes(d.model_rating)
  );
}

/**
 * Convert district number to district code
 * @param {number} districtNumber - The district number (e.g., 17)
 * @returns {string} District code (e.g., "CA-17")
 */
export function districtNumberToCode(districtNumber) {
  return `CA-${districtNumber}`;
}

/**
 * Convert district code to district number
 * @param {string} districtCode - The district code (e.g., "CA-17")
 * @returns {number|null} District number or null if invalid
 */
export function districtCodeToNumber(districtCode) {
  if (!isCaliforniaDistrict(districtCode)) return null;
  const num = parseInt(districtCode.replace('CA-', ''), 10);
  return isNaN(num) ? null : num;
}
